import React, { useCallback, useMemo, useRef } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import { SelectChangeEvent, Typography } from '@mui/material'
import clsx from 'clsx'
import {
  BoxV4,
  SelectV4,
  TableComponentV2,
  TextInputV4,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronsRight,
  faListCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { Dimension } from '../../../types/flyout'
import {
  buildDimensionsRenewalTable,
  DEFAULT_RENEWAL_COLUMNS,
  fieldInfoTooltip,
  PriceUpliftTypeOption,
  RENEWAL_HEADING_ICON_STYLE,
  RENEWAL_NUMBER_KEYS_TO_PREVENT,
  RENEWAL_PRICING_INFO_TOOLTIPS,
  RenewalCountOption,
  RenewalPricingTermsOption,
  RenewalSectionInfoIcon,
  renewalCountOptions,
  renewalPricingTermsOptions,
  priceUpliftTypeOptions,
  resolveUpliftPercent,
  type RenewalPricingFormValues,
} from '../../utils/renewalPricingTerms'
import { useStyles } from './PrivateOfferRenewalPricingTerms.styles'
import { PrivateOfferRenewalPricingTermsFaqs } from './PrivateOfferRenewalPricingTermsFaqs'

export const PrivateOfferRenewalPricingTerms: React.FC = () => {
  const { classes } = useStyles()
  const {
    values,
    setFieldValue,
    handleBlur,
    handleChange,
    touched,
    errors,
    validateField,
    setFieldTouched,
  } = useFormikContext() as FormikProps<RenewalPricingFormValues>

  const revalidateDefaultUplift = () => {
    void setFieldTouched('defaultUpliftPercent', true, false)
    void validateField('defaultUpliftPercent')
  }

  const handleRangeFieldBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    handleBlur(e)
    revalidateDefaultUplift()
  }

  const pricingTerms =
    values.renewalPricingTerms ?? RenewalPricingTermsOption.NO_RENEWAL
  const isPriceUplift = pricingTerms === RenewalPricingTermsOption.PRICE_UPLIFT
  const showRenewalCount = pricingTerms !== RenewalPricingTermsOption.NO_RENEWAL
  const isDefinite =
    showRenewalCount && values.renewalCountType === RenewalCountOption.DEFINITE
  const isIndefinite =
    showRenewalCount &&
    values.renewalCountType === RenewalCountOption.INDEFINITE
  const showOptOutDeadline = isDefinite || isIndefinite
  const showPriceUpliftFields = isPriceUplift && showOptOutDeadline
  const priceUpliftType = values.priceUpliftType ?? PriceUpliftTypeOption.FIXED
  const isFixedUplift =
    showPriceUpliftFields && priceUpliftType === PriceUpliftTypeOption.FIXED
  const isRangeUplift =
    showPriceUpliftFields && priceUpliftType === PriceUpliftTypeOption.RANGE

  const productDimensions = useMemo(
    () =>
      (values.dimensions ?? []).filter(
        (dimension: Dimension) =>
          dimension?.type === 'standard' || dimension?.type === 'custom'
      ),
    [values.dimensions]
  )
  const usageDimensions = useMemo(
    () =>
      (values.dimensions ?? []).filter(
        (dimension: Dimension) => dimension?.type === 'additional_usage'
      ),
    [values.dimensions]
  )
  const hasUsageDimensions = usageDimensions.length > 0
  // Figma: product dims renewal table is upfront-only; installment uses Renewal installments
  const showDimensionsRenewalTable =
    !values.flexiblePaymentTerms &&
    isPriceUplift &&
    showOptOutDeadline &&
    productDimensions.length > 0

  const upliftPercent = isPriceUplift
    ? resolveUpliftPercent({
        priceUpliftType,
        priceUpliftPercent: values.priceUpliftPercent,
        defaultUpliftPercent: values.defaultUpliftPercent,
      })
    : null

  const renewalColumnCount = useMemo(() => {
    const count = Number(values.renewalCount)
    if (isDefinite && count > 0) return count
    return DEFAULT_RENEWAL_COLUMNS
  }, [isDefinite, values.renewalCount])

  const tableScrollRef = useRef<HTMLDivElement>(null)
  const usageTableScrollRef = useRef<HTMLDivElement>(null)
  const scrollTable = useCallback((root: HTMLDivElement | null) => {
    if (!root) return
    const scroller =
      (root.querySelector('.MuiTableContainer-root') as HTMLElement | null) ??
      root
    scroller.scrollBy({ left: 160, behavior: 'smooth' })
  }, [])
  const scrollRenewalColumns = useCallback(
    () => scrollTable(tableScrollRef.current),
    [scrollTable]
  )
  const scrollUsageRenewalColumns = useCallback(
    () => scrollTable(usageTableScrollRef.current),
    [scrollTable]
  )

  const dimensionsRenewalTable = useMemo(
    () =>
      buildDimensionsRenewalTable({
        dimensions: productDimensions,
        renewalColumnCount,
        upliftPercent,
        classes,
      }),
    [
      classes.renewalPrice,
      classes.upliftArrow,
      classes.upliftBadge,
      productDimensions,
      renewalColumnCount,
      upliftPercent,
    ]
  )

  const usageDimensionsRenewalTable = useMemo(
    () =>
      buildDimensionsRenewalTable({
        dimensions: usageDimensions,
        renewalColumnCount,
        upliftPercent,
        classes,
      }),
    [
      classes.renewalPrice,
      classes.upliftArrow,
      classes.upliftBadge,
      usageDimensions,
      renewalColumnCount,
      upliftPercent,
    ]
  )
  // Figma: usage dims table on Price uplift — upfront and installments-with-usage
  const showUsageDimensionsRenewalTable =
    isPriceUplift &&
    showOptOutDeadline &&
    hasUsageDimensions &&
    usageDimensionsRenewalTable.objects.length > 0

  const pricingTermsField = useMemo(
    () => ({
      title: 'Renewal pricing terms',
      placeHolder: 'Renewal pricing terms',
      humanReadableKey: 'Renewal pricing terms',
      id: 'renewalPricingTerms',
      isRequired: false,
      isReadOnly: false,
      isCustom: false,
      defaultValue: RenewalPricingTermsOption.NO_RENEWAL,
      dataType: 'enum' as DataType,
      options: renewalPricingTermsOptions,
    }),
    []
  )

  const renewalCountField = useMemo(
    () => ({
      title: 'Renewal count',
      placeHolder: 'Renewal count',
      humanReadableKey: 'Renewal count',
      id: 'renewalCountType',
      isRequired: false,
      isReadOnly: false,
      isCustom: false,
      defaultValue: '',
      dataType: 'enum' as DataType,
      options: renewalCountOptions,
    }),
    []
  )

  const priceUpliftTypeField = useMemo(
    () => ({
      title: 'Price uplift type',
      placeHolder: 'Price uplift type',
      humanReadableKey: 'Price uplift type',
      id: 'priceUpliftType',
      isRequired: false,
      isReadOnly: false,
      isCustom: false,
      defaultValue: PriceUpliftTypeOption.FIXED,
      dataType: 'enum' as DataType,
      options: priceUpliftTypeOptions,
    }),
    []
  )

  const clearUpliftFields = useCallback(async () => {
    await setFieldValue('priceUpliftType', undefined)
    await setFieldValue('priceUpliftPercent', undefined)
    await setFieldValue('priceUpliftMinPercent', undefined)
    await setFieldValue('priceUpliftMaxPercent', undefined)
    await setFieldValue('adjustmentDeadlineDays', undefined)
    await setFieldValue('defaultUpliftPercent', undefined)
  }, [setFieldValue])

  const handlePricingTermsChange = useCallback(
    async (event: SelectChangeEvent) => {
      const next = event.target.value
      await setFieldValue('renewalPricingTerms', next)
      if (next === RenewalPricingTermsOption.NO_RENEWAL) {
        await setFieldValue('renewalCountType', undefined)
        await setFieldValue('renewalCount', undefined)
        await setFieldValue('optOutDeadlineDays', undefined)
        await clearUpliftFields()
      }
      if (next === RenewalPricingTermsOption.NO_PRICE_UPLIFT) {
        await clearUpliftFields()
      }
      if (next === RenewalPricingTermsOption.PRICE_UPLIFT) {
        await setFieldValue(
          'priceUpliftType',
          values.priceUpliftType ?? PriceUpliftTypeOption.FIXED
        )
      }
    },
    [setFieldValue, clearUpliftFields, values.priceUpliftType]
  )

  const handleRenewalCountTypeChange = useCallback(
    async (event: SelectChangeEvent) => {
      const next = event.target.value
      await setFieldValue('renewalCountType', next)
      if (next !== RenewalCountOption.DEFINITE) {
        await setFieldValue('renewalCount', undefined)
      }
    },
    [setFieldValue]
  )

  const handlePriceUpliftTypeChange = useCallback(
    async (event: SelectChangeEvent) => {
      const next = event.target.value
      await setFieldValue('priceUpliftType', next)
      if (next === PriceUpliftTypeOption.FIXED) {
        await setFieldValue('priceUpliftMinPercent', undefined)
        await setFieldValue('priceUpliftMaxPercent', undefined)
        await setFieldValue('adjustmentDeadlineDays', undefined)
        await setFieldValue('defaultUpliftPercent', undefined)
      } else {
        await setFieldValue('priceUpliftPercent', undefined)
      }
    },
    [setFieldValue]
  )

  const priceUpliftTypeSelect = (
    <div className={classes.field}>
      <SelectV4
        field={priceUpliftTypeField}
        value={priceUpliftType}
        onChange={handlePriceUpliftTypeChange}
        onBlur={handleBlur}
        showInsideInfoIcon
        showInsideInfoIconTooltip={fieldInfoTooltip(
          RENEWAL_PRICING_INFO_TOOLTIPS.priceUpliftType
        )}
      />
    </div>
  )

  return (
    <div data-testid="renewal-pricing-terms-section">
      <BoxV4
        heading="Renewal pricing terms"
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={RENEWAL_HEADING_ICON_STYLE}
          />
        }
        secondaryActions={<PrivateOfferRenewalPricingTermsFaqs />}
      >
        <div className={classes.fields}>
          <div className={classes.row}>
            <div className={classes.field}>
              <SelectV4
                field={pricingTermsField}
                value={pricingTerms}
                onChange={handlePricingTermsChange}
                onBlur={handleBlur}
                showInsideInfoIcon
                showInsideInfoIconTooltip={fieldInfoTooltip(
                  RENEWAL_PRICING_INFO_TOOLTIPS.renewalPricingTerms
                )}
              />
            </div>
            {showRenewalCount && (
              <div className={classes.field}>
                <SelectV4
                  field={renewalCountField}
                  value={values.renewalCountType ?? ''}
                  onChange={handleRenewalCountTypeChange}
                  onBlur={handleBlur}
                  showInsideInfoIcon
                  showInsideInfoIconTooltip={fieldInfoTooltip(
                    RENEWAL_PRICING_INFO_TOOLTIPS.renewalCount
                  )}
                />
              </div>
            )}
          </div>

          {showOptOutDeadline && (
            <div className={classes.row}>
              {isDefinite && (
                <div className={classes.field}>
                  <TextInputV4
                    keyName="renewalCount"
                    title="Number of renewals"
                    type="number"
                    placeHolder=""
                    value={values.renewalCount ?? ''}
                    updatedValue={values.renewalCount ?? ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isError={!!touched.renewalCount && !!errors.renewalCount}
                    errorText={
                      (!!touched.renewalCount && errors.renewalCount) || ''
                    }
                    isReadOnly={false}
                    isRequired={false}
                    keyToPrevent={RENEWAL_NUMBER_KEYS_TO_PREVENT}
                    showSpinner={false}
                    showInsideInfoIcon
                    showInsideInfoIconTooltip={fieldInfoTooltip(
                      RENEWAL_PRICING_INFO_TOOLTIPS.numberOfRenewals
                    )}
                  />
                </div>
              )}
              <div className={classes.field}>
                <TextInputV4
                  keyName="optOutDeadlineDays"
                  title="Opt out deadline in number of days"
                  type="number"
                  placeHolder=""
                  value={values.optOutDeadlineDays ?? ''}
                  updatedValue={values.optOutDeadlineDays ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isError={
                    !!touched.optOutDeadlineDays && !!errors.optOutDeadlineDays
                  }
                  errorText={
                    (!!touched.optOutDeadlineDays &&
                      errors.optOutDeadlineDays) ||
                    ''
                  }
                  isReadOnly={false}
                  isRequired={false}
                  keyToPrevent={RENEWAL_NUMBER_KEYS_TO_PREVENT}
                  showSpinner={false}
                  showInsideInfoIcon
                  showInsideInfoIconTooltip={fieldInfoTooltip(
                    RENEWAL_PRICING_INFO_TOOLTIPS.optOutDeadline
                  )}
                />
              </div>
              {showPriceUpliftFields && isIndefinite && priceUpliftTypeSelect}
            </div>
          )}

          {showPriceUpliftFields && isFixedUplift && (
            <div className={classes.row}>
              {isDefinite && priceUpliftTypeSelect}
              <div className={clsx(classes.field, classes.percentWithInfo)}>
                <TextInputV4
                  keyName="priceUpliftPercent"
                  title="Price uplift percentage"
                  type="number"
                  placeHolder=""
                  value={values.priceUpliftPercent ?? ''}
                  updatedValue={values.priceUpliftPercent ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isError={
                    !!touched.priceUpliftPercent && !!errors.priceUpliftPercent
                  }
                  errorText={
                    (!!touched.priceUpliftPercent &&
                      errors.priceUpliftPercent) ||
                    ''
                  }
                  isReadOnly={false}
                  isRequired={false}
                  keyToPrevent={RENEWAL_NUMBER_KEYS_TO_PREVENT}
                  showSpinner={false}
                  showInsideInfoIcon
                  showInsideInfoIconTooltip={fieldInfoTooltip(
                    RENEWAL_PRICING_INFO_TOOLTIPS.priceUpliftPercent
                  )}
                />
              </div>
            </div>
          )}

          {isRangeUplift && (
            <>
              {isDefinite && (
                <div className={classes.row}>{priceUpliftTypeSelect}</div>
              )}
              <div className={classes.row}>
                <div className={clsx(classes.field, classes.percentSuffix)}>
                  <TextInputV4
                    keyName="priceUpliftMinPercent"
                    title="Minimum uplift percentage"
                    type="number"
                    placeHolder=""
                    value={values.priceUpliftMinPercent ?? ''}
                    updatedValue={values.priceUpliftMinPercent ?? ''}
                    onChange={handleChange}
                    onBlur={handleRangeFieldBlur}
                    isError={
                      !!touched.priceUpliftMinPercent &&
                      !!errors.priceUpliftMinPercent
                    }
                    errorText={
                      (!!touched.priceUpliftMinPercent &&
                        errors.priceUpliftMinPercent) ||
                      ''
                    }
                    isReadOnly={false}
                    isRequired={false}
                    keyToPrevent={RENEWAL_NUMBER_KEYS_TO_PREVENT}
                    showSpinner={false}
                  />
                </div>
                <div className={clsx(classes.field, classes.percentSuffix)}>
                  <TextInputV4
                    keyName="priceUpliftMaxPercent"
                    title="Maximum uplift percentage"
                    type="number"
                    placeHolder=""
                    value={values.priceUpliftMaxPercent ?? ''}
                    updatedValue={values.priceUpliftMaxPercent ?? ''}
                    onChange={handleChange}
                    onBlur={handleRangeFieldBlur}
                    isError={
                      !!touched.priceUpliftMaxPercent &&
                      !!errors.priceUpliftMaxPercent
                    }
                    errorText={
                      (!!touched.priceUpliftMaxPercent &&
                        errors.priceUpliftMaxPercent) ||
                      ''
                    }
                    isReadOnly={false}
                    isRequired={false}
                    keyToPrevent={RENEWAL_NUMBER_KEYS_TO_PREVENT}
                    showSpinner={false}
                  />
                </div>
              </div>
              <div className={classes.row}>
                <div className={classes.field}>
                  <TextInputV4
                    keyName="adjustmentDeadlineDays"
                    title="Adjustment deadline in number of days"
                    type="number"
                    placeHolder=""
                    value={values.adjustmentDeadlineDays ?? ''}
                    updatedValue={values.adjustmentDeadlineDays ?? ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isError={
                      !!touched.adjustmentDeadlineDays &&
                      !!errors.adjustmentDeadlineDays
                    }
                    errorText={
                      (!!touched.adjustmentDeadlineDays &&
                        errors.adjustmentDeadlineDays) ||
                      ''
                    }
                    isReadOnly={false}
                    isRequired={false}
                    keyToPrevent={RENEWAL_NUMBER_KEYS_TO_PREVENT}
                    showSpinner={false}
                    showInsideInfoIcon
                    showInsideInfoIconTooltip={fieldInfoTooltip(
                      RENEWAL_PRICING_INFO_TOOLTIPS.adjustmentDeadline
                    )}
                  />
                </div>
                <div className={clsx(classes.field, classes.percentWithInfo)}>
                  <TextInputV4
                    keyName="defaultUpliftPercent"
                    title="Default uplift percentage"
                    type="number"
                    placeHolder=""
                    value={values.defaultUpliftPercent ?? ''}
                    updatedValue={values.defaultUpliftPercent ?? ''}
                    onChange={handleChange}
                    onBlur={handleRangeFieldBlur}
                    isError={
                      !!touched.defaultUpliftPercent &&
                      !!errors.defaultUpliftPercent
                    }
                    errorText={
                      (!!touched.defaultUpliftPercent &&
                        errors.defaultUpliftPercent) ||
                      ''
                    }
                    isReadOnly={false}
                    isRequired={false}
                    keyToPrevent={RENEWAL_NUMBER_KEYS_TO_PREVENT}
                    showSpinner={false}
                    showInsideInfoIcon
                    showInsideInfoIconTooltip={fieldInfoTooltip(
                      RENEWAL_PRICING_INFO_TOOLTIPS.defaultUpliftPercent
                    )}
                  />
                </div>
              </div>
            </>
          )}

          {showDimensionsRenewalTable && (
            <div
              className={classes.tableSection}
              data-testid="renewal-dimensions-price-table"
            >
              <Typography className={classes.tableTitle}>
                <span className={classes.headingWithInfo}>
                  {"Product dimensions' renewal price based on above selection"}
                  <RenewalSectionInfoIcon
                    description={
                      RENEWAL_PRICING_INFO_TOOLTIPS.productDimensionsTable
                    }
                    testId="renewal-product-dimensions-table-info"
                    className={classes.info}
                  />
                </span>
              </Typography>
              <div className={classes.tableScrollWrap}>
                <div className={classes.tableScroll} ref={tableScrollRef}>
                  <TableComponentV2
                    objects={
                      dimensionsRenewalTable.objects as Record<
                        string,
                        string | boolean | JSX.Element | null
                      >[]
                    }
                    mapping={dimensionsRenewalTable.mapping}
                    count={dimensionsRenewalTable.objects.length}
                    fetchMoreData={() => void 0}
                    onChangeCheckbox={() => void 0}
                    selectedItem={[]}
                    showCheckbox={false}
                    isBasicTable={true}
                    keyName="renewal-dimensions-price-table"
                  />
                </div>
                <button
                  type="button"
                  className={classes.scrollNext}
                  onClick={scrollRenewalColumns}
                  aria-label="Next renewal columns"
                  data-testid="renewal-table-scroll-next"
                >
                  <FontAwesomeIcon icon={faChevronsRight} />
                </button>
              </div>
            </div>
          )}

          {showUsageDimensionsRenewalTable && (
            <div
              className={classes.tableSection}
              data-testid="renewal-usage-dimensions-price-table"
            >
              <Typography className={classes.tableTitle}>
                <span className={classes.headingWithInfo}>
                  {"Usage dimensions' renewal price based on above selection"}
                  <RenewalSectionInfoIcon
                    description={
                      RENEWAL_PRICING_INFO_TOOLTIPS.usageDimensionsTable
                    }
                    testId="renewal-usage-dimensions-table-info"
                    className={classes.info}
                  />
                </span>
              </Typography>
              <div className={classes.tableScrollWrap}>
                <div className={classes.tableScroll} ref={usageTableScrollRef}>
                  <TableComponentV2
                    objects={
                      usageDimensionsRenewalTable.objects as Record<
                        string,
                        string | boolean | JSX.Element | null
                      >[]
                    }
                    mapping={usageDimensionsRenewalTable.mapping}
                    count={usageDimensionsRenewalTable.objects.length}
                    fetchMoreData={() => void 0}
                    onChangeCheckbox={() => void 0}
                    selectedItem={[]}
                    showCheckbox={false}
                    isBasicTable={true}
                    keyName="renewal-usage-dimensions-price-table"
                  />
                </div>
                <button
                  type="button"
                  className={classes.scrollNext}
                  onClick={scrollUsageRenewalColumns}
                  aria-label="Next usage renewal columns"
                  data-testid="renewal-usage-table-scroll-next"
                >
                  <FontAwesomeIcon icon={faChevronsRight} />
                </button>
              </div>
            </div>
          )}
        </div>
      </BoxV4>
    </div>
  )
}
